import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { isEmpty } from 'lodash/lang';
import { omit } from 'lodash/object';
import { i18nt } from 'root';
import { NSC_STUDY, NSPP_PATIENT } from 'services/i18n/namespaces';

import { generateUrlByKey, useCurrentRoute } from 'components/root/router';

import { PatientStatusJourneyApi } from '../../../../../../api';
import Select from '../../../../../../common/data-entry/Select';
import { findPatientStatusNameByKey } from '../../constants';
import { PatientInfoContext } from '../PatientInfoContext';

import { InformedConsentWidget } from './InformedConsentWidget/InformedConsentWidget';
import LogsWidget from './LogsWidget/LogsWidget';
import ProgressNotesWidget from './ProgressNotesWidget/ProgressNotesWidget';

import './PatientInfoSidebar.scss';

export default function PatientInfoSidebarMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentRoute = useCurrentRoute();
  const ssuPatientId = currentRoute.params?.ssuPatientId;
  const { studyHistory, currentStudy, forms } = useContext(PatientInfoContext);
  const [alertStatus, setAlertStatus] = useState(null);

  const studies = useMemo(
    function() {
      return (
        studyHistory &&
        studyHistory.map(function(study) {
          return { ...study, disabled: !study.granted };
        })
      );
    },
    [studyHistory]
  );
  const initialValue = useMemo(
    function() {
      return studies?.find(study => study.ssuPatientId === ssuPatientId);
    },
    [ssuPatientId, studies]
  );

  useEffect(() => {
    if (ssuPatientId) {
      PatientStatusJourneyApi.getCurrentPatientStatusType(ssuPatientId).then(({ data }) => {
        setAlertStatus(data);
      });
    }
  }, [ssuPatientId]);

  return (
    <div className="eds-patient-info-sidebar">
      <div className="pis-ssu-patient-select">
        {studyHistory && (
          <Select
            data-testid="study-dropdown"
            className="eui-filter-control-study-pcn"
            value={initialValue}
            label={t(`${NSC_STUDY}:selectStudy`)}
            dataSource={studies}
            optionLabelKey="studyName"
            optionValueKey="ssuPatientId"
            customOptionTemplateFunction={customOptionTemplateFunction}
            customSelectedValueTemplateFunction={study => study[0].studyName}
            onChange={value => {
              if (value?.ssuPatientId && currentRoute.params.ssuPatientId !== value.ssuPatientId) {
                navigate(
                  generateUrlByKey(currentRoute.key, {
                    ssuPatientId: value.ssuPatientId,
                    ...omit(currentRoute.params, 'ssuPatientId')
                  })
                );
              }
            }}
            clearable={false}
          />
        )}
      </div>
      <div className="pcn-indicator" data-testid="pcn-indicator">
        PCN: {initialValue?.pcn}
      </div>
      <div className={'pis-status-indicator'}>
        {`${t('status', { ns: NSPP_PATIENT })}:`}
        <span>
          <Link to={generateUrlByKey(`${currentRoute.key}.Patient Status`, { ssuPatientId, ...currentRoute.params })}>
            {currentStudy?.patientStatusId
              ? t(`${NSPP_PATIENT}:statuses.${findPatientStatusNameByKey(currentStudy.patientStatusId)}`)
              : '---'}
          </Link>
        </span>
        {alertStatus?.hasSkipped && <div className="basic-dot skipped" />}
        {alertStatus?.hasFailed && <div className="basic-dot failed" />}
      </div>
      {!isEmpty(forms.completedIcf) && <InformedConsentWidget />}
      <LogsWidget ssuPatientId={ssuPatientId} />
      <ProgressNotesWidget ssuPatientId={ssuPatientId} />
    </div>
  );
}

function customOptionTemplateFunction({ studyName, patientStatusId, pcn }) {
  return (
    <React.Fragment>
      <div className="eui-filter-control-study-pcn-label">{pcn}</div>
      <div>
        {studyName}
        {patientStatusId && (
          <span className="patient-status">
            {statusNormalizer(i18nt(`${NSPP_PATIENT}:statuses.${findPatientStatusNameByKey(patientStatusId)}`))}
          </span>
        )}
      </div>
    </React.Fragment>
  );
}

function statusNormalizer(status) {
  return capitaliseFirstLetter(status.toLowerCase());
}

function capitaliseFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}
